import { toast } from "react-toastify";

// React error boundaries only catch render/lifecycle errors. An exception in
// a setTimeout, a DOM event handler, or an unhandled promise rejection (e.g.
// a fetch nobody awaited/caught) currently has no boundary and no log --
// it just vanishes. This is the last-resort net for those.

const IGNORED_MESSAGE_PATTERNS = [
  // Benign browser noise, not an app bug -- see
  // https://stackoverflow.com/q/49384120
  /ResizeObserver loop/i,
  // Cross-origin scripts (browser extensions, ad blockers, etc.) report a
  // bare "Script error." with no detail -- nothing we can act on.
  /^Script error\.?$/i,
];

const IGNORED_REJECTION_NAMES = new Set(["AbortError", "CanceledError"]);
const IGNORED_REJECTION_CODES = new Set(["ERR_CANCELED"]);

const shouldIgnoreMessage = (message) =>
  IGNORED_MESSAGE_PATTERNS.some((pattern) => pattern.test(message || ""));

const shouldIgnoreRejection = (reason) =>
  IGNORED_REJECTION_NAMES.has(reason?.name) ||
  IGNORED_REJECTION_CODES.has(reason?.code);

const TOAST_OPTIONS = { position: "top-right", autoClose: 5000 };
const NOTIFY_MESSAGE =
  "Something went wrong. If this keeps happening, try reloading the page.";

// Aborted/canceled requests fire constantly during normal navigation (a page
// unmounts mid-fetch); a burst of real errors shouldn't spam toasts either.
let lastNotifiedAt = 0;
const notifyOnce = () => {
  const now = Date.now();
  if (now - lastNotifiedAt < 4000) return;
  lastNotifiedAt = now;
  try {
    toast.error(NOTIFY_MESSAGE, TOAST_OPTIONS);
  } catch (toastError) {
    console.error("Failed to show error toast:", toastError);
  }
};

export function initGlobalErrorHandling() {
  window.addEventListener("error", (event) => {
    if (shouldIgnoreMessage(event.message)) return;
    console.error("Uncaught error:", event.error || event.message);
    notifyOnce();
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    if (shouldIgnoreRejection(reason) || shouldIgnoreMessage(reason?.message)) return;
    console.error("Unhandled promise rejection:", reason);
    notifyOnce();
  });
}
